
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { RenameOrganizationDialog } from '@/components/organization/RenameOrganizationDialog';
import { CreateOrganizationDialog } from '@/components/organization/CreateOrganizationDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';
import { useSubscription } from '@/hooks/use-subscription';

interface Workspace {
  id: string;
  name: string;
  organization_id: string;
}

interface Organization {
  id: string;
  name: string;
  workspaces: Workspace[];
}

export default function ManageOrganizations() {
  const { t } = useTranslation();
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null);
  const [targetOrganization, setTargetOrganization] = useState<string | null>(null);
  const { plan } = useSubscription();

  const { data: organizations, refetch, isLoading } = useQuery({
    queryKey: ['organizations-with-workspaces'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { data, error } = await supabase.rpc('get_organizations_with_workspaces');
        if (error) {
          console.error('Error fetching organizations:', error);
          toast.error(t('common.organizations.errorFetching'));
          throw error;
        }
        
        const orgsData = data as unknown as Organization[];
        return orgsData;
      } catch (error) {
        console.error('Error:', error);
        toast.error(t('common.organizations.errorFetching'));
        throw error;
      }
    },
    retry: 1
  });

  const remainingOrganizations = plan ? plan.max_organizations - (organizations?.length || 0) : 0;

  const handleMoveWorkspace = async () => {
    if (!selectedWorkspace || !targetOrganization) {
      toast.error(t('Please select a workspace and target organization'));
      return;
    }

    try {
      const { error } = await supabase
        .from('workspaces')
        .update({ organization_id: targetOrganization })
        .eq('id', selectedWorkspace);

      if (error) {
        console.error('Error moving workspace:', error);
        toast.error(t('Error moving workspace'));
        return;
      }

      toast.success(t('Workspace moved successfully'));
      refetch();
      setSelectedWorkspace(null);
      setTargetOrganization(null);
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('Error moving workspace'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('Manage Organizations')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('You have')} {organizations?.length || 0} {t('organizations')} ({remainingOrganizations} {t('remaining')})
          </p>
        </div>
        <CreateOrganizationDialog onOrganizationCreated={refetch} />
      </div>
      
      {organizations?.length === 0 ? (
        <div className="text-center py-8 border rounded-lg">
          <p className="text-muted-foreground">
            {t('No organizations yet. Create your first one!')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {organizations?.map((org) => (
            <div key={org.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{org.name}</h2>
                <RenameOrganizationDialog
                  organizationId={org.id}
                  currentName={org.name}
                  onRename={refetch}
                />
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t('Workspaces')}</h3>
                {org.workspaces?.length === 0 ? (
                  <p className="text-sm text-muted-foreground pl-4">
                    {t('No workspaces in this organization')}
                  </p>
                ) : (
                  org.workspaces?.map((workspace) => (
                    <div key={workspace.id} className="flex items-center justify-between pl-4">
                      <span>{workspace.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedWorkspace(workspace.id)}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedWorkspace && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <span className="text-sm font-medium">{t('Move workspace to:')}</span>
            <Select
              value={targetOrganization || ''}
              onValueChange={setTargetOrganization}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('Select organization')} />
              </SelectTrigger>
              <SelectContent>
                {organizations?.map((org) => (
                  <SelectItem 
                    key={org.id} 
                    value={org.id}
                    disabled={org.workspaces.some(w => w.id === selectedWorkspace)}
                  >
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={handleMoveWorkspace}
              disabled={!targetOrganization}
            >
              {t('Move')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedWorkspace(null);
                setTargetOrganization(null);
              }}
            >
              {t('Cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
